import Phaser from "phaser";
import { Client, type Room } from "@colyseus/sdk";
import {
  ITEM_ICONS,
  TECHNIQUE_ICONS,
  TECHNIQUE_SCROLL_RARITY_ICONS,
} from "@ninja/asset-catalog";
import { type InstanceState, ROOM_MESSAGES } from "@ninja/contracts";
import {
  BARRACKS_LEVELS,
  RARITY_PRESENTATION,
  barracksLevelDefinition,
} from "@ninja/domain";
import {
  DEFAULT_MAP_ID,
  itemDefinition,
  loadMapDefinition,
  TECHNIQUES,
  type EquipmentSlot,
} from "@ninja/game-content";
import {
  GameSessionLifecycle,
  type GameMountProgress,
  type GameRoom,
  type GameMode,
  type RegionTransitionRequest,
} from "./game/GameSessionLifecycle.js";
import { GameScene } from "./game/scenes/GameScene.js";
import { BossEntryConfirmationDialog } from "./game/ui/BossEntryConfirmationDialog.js";
import {
  NinjaDetailsDialog,
  type NinjaDetailsProfile,
  type NinjaDetailsRealtimeState,
} from "./game/ui/NinjaDetailsDialog.js";
import {
  JutsuLoadoutTab,
  type JutsuLibraryData,
  type JutsuLoadoutSlot,
} from "./ui/JutsuLoadoutTab.js";
import {
  TechniqueArchivePanel,
  type TechniqueArchiveNinja,
} from "./ui/TechniqueArchivePanel.js";
import { NinjaRosterPanel } from "./ui/NinjaRosterPanel.js";
import { AcademyPage } from "./ui/AcademyPage.js";
import { renderBrandIcon } from "./ui/BrandIcon.js";
import { HospitalPage } from "./ui/HospitalPage.js";
import { WarehousePage } from "./ui/WarehousePage.js";
import { SectionTabs } from "./ui/SectionTabs.js";
import { GameLoadingOverlay } from "./ui/GameLoadingOverlay.js";
import {
  mountMapSelectionPage,
  type MapSelectionEntry,
} from "./ui/MapSelectionPage.js";
import type { OwnedItemView, WarehouseView } from "./ui/warehouseTypes.js";
import {
  describeItemProgression,
  formatItemEnhancement,
  formatItemProgression,
  formatItemTier,
} from "./ui/itemProgression.js";
import {
  bindNinjaOutfitImageFallbacks,
  renderNinjaOutfitImage,
  renderNinjaOutfitPortrait as portrait,
} from "./ui/NinjaOutfitPortrait.js";
import {
  mountDashboardShell,
  type DashboardPage,
} from "./ui/DashboardShell.js";
import { escapeHtml } from "./ui/escapeHtml.js";
import "./ui/app-theme.css";
import type { LandingCredentials, LandingPage } from "./landing/LandingPage.js";
import { worldPointToTile } from "./game/tile-coordinates.js";
import "./game/ui/boss-entry-confirmation-dialog.css";
import "./style.css";
import "./ui/ninja-roster.css";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001";
const REALTIME_URL = import.meta.env.VITE_REALTIME_URL ?? "ws://localhost:2567";
interface PlayerSummary {
  ninjaCount: number;
  activeSquadCount: number;
  rosterCapacity: number;
  externalCapacity: number;
  academy: {
    foundation_remaining: number;
  };
  mapAccess: MapSelectionEntry[];
}

const app = document.querySelector<HTMLDivElement>("#app");
bindNinjaOutfitImageFallbacks(document);
if (!app) throw new Error("App root element is missing");

interface Ninja {
  id: string;
  name: string;
  appearance_id: string;
  rarity: keyof typeof RARITY_PRESENTATION;
  rank: string;
  level: number;
  total_experience: string | number;
  current_health: number;
  maximum_health: number;
  current_chakra: number;
  maximum_chakra: number;
  attributes?: Record<string, number>;
  proficiencies?: Array<{ type: string; level: number; experience: number }>;
  life_status?: "available" | "incapacitated" | "hospitalized" | "dismissed";
  activity_lock?: string | null;
  hospital_case_status?: "waiting" | "treating" | null;
  hospital_ready_at?: string | null;
}

interface BarracksView {
  barracks: {
    level: number;
    maxLevel: number;
    rosterCapacity: number;
    externalCapacity: number;
    mobilizationPresets: number;
    levelDefinitions: typeof BARRACKS_LEVELS;
  };
  rosterCount: number;
  foundationRemaining: number;
  ninjas: Array<{
    id: string;
    name: string;
    appearanceId: string;
    rarity: string;
    level: number;
    lifeStatus: string;
    inActivity: boolean;
    inActiveSquad: boolean;
    canDismiss: boolean;
  }>;
  presets: Array<{
    slot: number;
    name: string;
    mapId: string;
    ninjaIds: string[];
    ninjas: Array<{ id: string; name: string; appearanceId: string }>;
  }>;
  maps: Array<{
    id: string;
    name: string;
    status: "playable" | "preview";
    unlocked: boolean;
    lockReason: string | null;
  }>;
}

interface NinjaInventoryView {
  ninja: {
    id: string;
    name: string;
    appearanceId: string;
    rarity: keyof typeof RARITY_PRESENTATION;
    rank: string;
    level: number;
    currentHealth?: number;
    maximumHealth: number;
    currentChakra: number;
    maximumChakra: number;
    attributes: Record<string, number>;
    lifeStatus?: "available" | "incapacitated" | "hospitalized" | "dismissed";
    activityLock?: string | null;
  };
  equipmentSlots: Array<{
    slot: EquipmentSlot;
    item: OwnedItemView | null;
  }>;
  backpack: {
    items: OwnedItemView[];
    assignmentId: string | null;
  };
}
interface NinjaTechniqueLibraryView {
  known: Array<{ technique_id: string; technique_level: number }>;
  equipped: Array<{ technique_id: string; slot_index: number }>;
}
interface User {
  id: string;
  username: string;
}
type InstanceStateLike = InstanceState;
let currentUser: User | null = null;
let landingPage: LandingPage | null = null;
let landingPageLoadVersion = 0;
let currentRoom: Room<InstanceStateLike> | null = null;
let currentGame: Phaser.Game | null = null;
let leavingGame = false;
const intentionalRoomExits = new WeakSet<object>();
const ROOM_TOKEN_KEY = "ninja-idle-room-token";
const REGION_TRANSITION_KEY = "ninja-idle-region-transition";
const gameLoadingOverlay = new GameLoadingOverlay();
let academyPage: AcademyPage | null = null;
let hospitalPage: HospitalPage | null = null;
let warehousePage: WarehousePage | null = null;

const gameSessionLifecycle = new GameSessionLifecycle({
  request: api,
  createClient: () => new Client(REALTIME_URL),
  loading: gameLoadingOverlay,
  getRoom: () => currentRoom,
  setRoom: (room) => {
    currentRoom = room;
  },
  mountGame: (room, mode, mapId, progress) =>
    showGame(room, mode, mapId, progress),
  destroyGame: () => {
    currentGame?.destroy(true);
    currentGame = null;
  },
  setLeavingGame: (leaving) => {
    leavingGame = leaving;
  },
  markRoomExitIntentional: (room) => {
    intentionalRoomExits.add(room);
  },
  setPlaying: (playing) => {
    document.body.classList.toggle("playing", playing);
  },
  saveRoomToken: (token) => sessionStorage.setItem(ROOM_TOKEN_KEY, token),
  clearRoomToken: () => sessionStorage.removeItem(ROOM_TOKEN_KEY),
  getPendingRegionTransition: () => {
    const stored = sessionStorage.getItem(REGION_TRANSITION_KEY);
    if (!stored) return null;
    try {
      const value = JSON.parse(stored) as Partial<RegionTransitionRequest>;
      if (
        typeof value.proof === "string" &&
        typeof value.fromMapId === "string" &&
        typeof value.toMapId === "string" &&
        typeof value.spawnId === "string" &&
        typeof value.startedAt === "number"
      )
        return value as RegionTransitionRequest;
    } catch {
      // Drop invalid session state and let normal room recovery continue.
    }
    sessionStorage.removeItem(REGION_TRANSITION_KEY);
    return null;
  },
  savePendingRegionTransition: (transition) =>
    sessionStorage.setItem(REGION_TRANSITION_KEY, JSON.stringify(transition)),
  clearPendingRegionTransition: () =>
    sessionStorage.removeItem(REGION_TRANSITION_KEY),
  setEntryButtonDisabled: (mode, disabled, button) => {
    const target =
      button ??
      document.querySelector<HTMLButtonElement>(`[data-mode="${mode}"]`);
    if (target) target.disabled = disabled;
  },
  showEntryError: (message) => {
    console.error("Could not enter game instance", message);
    const gameMessage = document.querySelector<HTMLElement>("#game-message");
    if (gameMessage) {
      gameMessage.textContent = `Could not enter the game: ${message}`;
      gameMessage.classList.add("visible");
    } else {
      setNotice(message);
    }
  },
  showTransitionError: (message) => {
    const gameMessage = document.querySelector<HTMLElement>("#game-message");
    if (!gameMessage) return;
    gameMessage.textContent = message;
    gameMessage.classList.add("visible");
  },
  showDashboard: () => showDashboard(),
  setNotice,
});

async function api<T>(
  path: string,
  init: RequestInit = {},
  allowRefresh = true,
): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: { "content-type": "application/json", ...(init.headers ?? {}) },
  });
  if (response.status === 401 && allowRefresh && !path.startsWith("/auth/")) {
    const refreshed = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
    if (refreshed.ok) return api<T>(path, init, false);
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
    } | null;
    throw new Error(
      body?.error?.message ?? `Request failed (${response.status})`,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

function setNotice(message: string, kind: "error" | "success" = "error"): void {
  const element = document.querySelector<HTMLElement>("#notice");
  if (element) {
    element.textContent = message;
    element.dataset.kind = kind;
  }
}

function destroyLandingPage(): void {
  landingPageLoadVersion += 1;
  landingPage?.destroy();
  landingPage = null;
}

async function showLanding(): Promise<void> {
  destroyHospitalPage();
  destroyWarehousePage();
  academyPage?.destroy();
  academyPage = null;
  currentUser = null;
  destroyLandingPage();
  const loadVersion = landingPageLoadVersion;
  app!.innerHTML =
    '<main class="landing-loading" role="status" aria-live="polite">Loading village…</main>';

  try {
    const { LandingPage } = await import("./landing/LandingPage.js");
    if (loadVersion !== landingPageLoadVersion) return;
    landingPage = new LandingPage(app!, {
      onAuthenticate: async (credentials: LandingCredentials) => {
        currentUser =
          credentials.mode === "login"
            ? (
                await api<{ user: User }>("/auth/login", {
                  method: "POST",
                  body: JSON.stringify({
                    email: credentials.email,
                    password: credentials.password,
                  }),
                })
              ).user
            : (
                await api<{ user: User }>("/auth/register", {
                  method: "POST",
                  body: JSON.stringify({
                    email: credentials.email,
                    username: credentials.username,
                    password: credentials.password,
                  }),
                })
              ).user;
        await showDashboard();
      },
    });
  } catch (error) {
    if (loadVersion !== landingPageLoadVersion) return;
    console.error("Could not load the landing page", error);
    app!.innerHTML =
      '<main class="landing-loading" role="alert">Could not load the landing page. Refresh to try again.</main>';
  }
}

async function restoreSession(): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/auth/me`, {
      credentials: "include",
    });
    if (!response.ok) {
      const refreshed = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!refreshed.ok) return false;
    }
    const result = await api<{ user: User }>("/auth/me");
    currentUser = result.user;
    return true;
  } catch {
    return false;
  }
}

async function showDashboard(section: DashboardPage = "home"): Promise<void> {
  destroyHospitalPage();
  destroyWarehousePage();
  academyPage?.destroy();
  academyPage = null;
  if (!currentUser) return showLanding();
  const rosterRequest =
    section === "academy" ||
    section === "roster" ||
    section === "technique-archive"
      ? api<{ ninjas: Ninja[] }>("/ninjas")
      : Promise.resolve({ ninjas: [] as Ninja[] });
  const [summary, roster] = await Promise.all([
    api<PlayerSummary>("/players/me/summary"),
    rosterRequest,
  ]);
  const maps = [...summary.mapAccess].sort(
    (left, right) =>
      Number(left.id !== DEFAULT_MAP_ID) -
        Number(right.id !== DEFAULT_MAP_ID) ||
      left.name.localeCompare(right.name),
  );
  destroyLandingPage();
  const username = currentUser.username;
  const content = mountDashboardShell(app!, {
    username,
    ninjaCount: summary.ninjaCount,
    academyFoundationRemaining: summary.academy.foundation_remaining,
    selectedPage: section,
    onLogout: () => {
      void (async () => {
        await api("/auth/logout", { method: "POST" }).catch(() => undefined);
        void showLanding();
      })();
    },
    onPageSelect: (page) => void showDashboard(page),
  });
  if (section === "academy") return showAcademy(content, roster.ninjas);
  if (section === "roster") return showRoster(content, roster.ninjas);
  if (section === "barracks") return showBarracks(content);
  if (section === "hospital") return showHospital(content);
  if (section === "warehouse") return showWarehouse(content);
  if (section === "technique-archive")
    return showTechniqueArchive(content, roster.ninjas);
  mountMapSelectionPage(content, {
    maps,
    defaultMapId: DEFAULT_MAP_ID,
    activeSquadCount: summary.activeSquadCount,
    externalCapacity: summary.externalCapacity,
    ninjaCount: summary.ninjaCount,
    rosterCapacity: summary.rosterCapacity,
    onGameModeSelect: (mode, mapId, button) =>
      void gameSessionLifecycle.enterGame(mode, mapId, button),
  });
}

function showTechniqueArchive(content: HTMLElement, ninjas: Ninja[]): void {
  const archiveNinjas: TechniqueArchiveNinja[] = ninjas.map((ninja) => ({
    id: ninja.id,
    name: ninja.name,
    appearance_id: ninja.appearance_id,
    rarity: ninja.rarity,
    rank: ninja.rank,
    level: ninja.level,
    life_status: ninja.life_status,
    activity_lock: ninja.activity_lock,
  }));
  void new TechniqueArchivePanel(content, archiveNinjas, api).mount();
}

async function showRoster(
  content: HTMLElement,
  ninjas: Ninja[],
): Promise<void> {
  const [squadResult, barracksView] = await Promise.all([
    api<{ ninjas: Array<{ id: string }> }>("/squads/active"),
    api<BarracksView>("/barracks"),
  ]);
  new NinjaRosterPanel({
    root: content,
    ninjas,
    savedActiveNinjaIds: squadResult.ninjas.map((ninja) => ninja.id),
    rosterCapacity: barracksView.barracks.rosterCapacity,
    squadCapacity: barracksView.barracks.externalCapacity,
    barracksLevel: barracksView.barracks.level,
    saveActiveSquad: async (ninjaIds) => {
      await api("/squads/active", {
        method: "PUT",
        body: JSON.stringify({ ninjaIds }),
      });
    },
    openInventory: (ninjaId) => {
      const ninja = ninjas.find((entry) => entry.id === ninjaId);
      if (ninja) void showNinjaInventory(ninja);
    },
    openBarracks: () => void showDashboard("barracks"),
  }).mount();
}

async function showBarracks(content: HTMLElement): Promise<void> {
  try {
    const view = await api<BarracksView>("/barracks");
    const currentLevel = barracksLevelDefinition(view.barracks.level);
    const levelRows = view.barracks.levelDefinitions
      .map((level) => {
        const current = level.level === view.barracks.level;
        return `<tr class="${current ? "current-level" : ""}"><td>Lv. ${level.level}${current ? '<span class="building-level-current-tag">CURRENT</span>' : ""}</td><td>${level.rosterCapacity}</td><td>${level.externalCapacity}</td><td>${level.mobilizationPresets}</td></tr>`;
      })
      .join("");
    const presetBySlot = new Map(
      view.presets.map((preset) => [preset.slot, preset]),
    );
    const mapOptions = view.maps
      .map(
        (map) =>
          `<option value="${escapeHtml(map.id)}" ${map.unlocked ? "" : "disabled"}>${escapeHtml(map.name)}${map.status === "preview" ? " · Preview" : ""}${map.unlocked ? "" : " · Locked"}</option>`,
      )
      .join("");
    const presetCards = Array.from(
      { length: currentLevel.mobilizationPresets },
      (_, index) => {
        const slot = index + 1;
        const preset = presetBySlot.get(slot);
        const selected = new Set(preset?.ninjaIds ?? []);
        const rosterOptions = view.ninjas
          .map((ninja) => {
            const isSelected = selected.has(ninja.id);
            const available =
              ninja.lifeStatus === "available" && !ninja.inActivity;
            const disabled = !available && !isSelected;
            const status = available
              ? "Available"
              : ninja.inActivity
                ? "In field operation"
                : ninja.lifeStatus;
            return `<label class="barracks-preset-ninja ${disabled ? "unavailable" : ""}"><input type="checkbox" name="ninjaId" value="${ninja.id}" ${isSelected ? "checked" : ""} ${disabled ? "disabled" : ""} /><span>${escapeHtml(ninja.name)}<small>${escapeHtml(status)} · Lv ${ninja.level}</small></span></label>`;
          })
          .join("");
        return `<form class="barracks-preset-card" data-barracks-preset="${slot}"><div class="barracks-preset-heading"><div><p class="eyebrow">SAVED MOBILIZATION</p><h3>${preset ? escapeHtml(preset.name) : `Preset ${slot}`}</h3></div><span class="barracks-preset-slot">SLOT ${slot}</span></div><label class="barracks-field">Preset name<input name="name" value="${escapeHtml(preset?.name ?? `Mobilization ${slot}`)}" minlength="2" maxlength="32" required /></label><label class="barracks-field">Destination<select name="mapId">${mapOptions}</select></label><div class="barracks-preset-ninjas"><span class="eyebrow">SQUAD · UP TO ${currentLevel.externalCapacity}</span>${rosterOptions || '<p class="muted">Recruit ninjas before creating a preset.</p>'}</div><div class="barracks-preset-actions"><button type="submit" class="button button-outline">Save preset</button>${preset ? `<button type="button" class="button button-primary" data-activate-preset="${slot}">Activate &amp; deploy</button><button type="button" class="text-button" data-delete-preset="${slot}">Delete</button>` : ""}</div></form>`;
      },
    ).join("");
    const dismissalRows = view.ninjas
      .map((ninja) => {
        const status = ninja.inActivity
          ? "In field operation"
          : ninja.lifeStatus === "available"
            ? ninja.inActiveSquad
              ? "Available · active squad"
              : "Available"
            : ninja.lifeStatus;
        const disabledReason =
          view.foundationRemaining > 0
            ? "Complete the guided Foundation recruits first."
            : view.rosterCount <= 1
              ? "The last village ninja cannot be dismissed."
              : ninja.inActivity
                ? "Return from the field operation first."
                : ninja.lifeStatus !== "available"
                  ? "Only available ninjas can be dismissed."
                  : "";
        return `<article class="barracks-ninja-row"><div class="barracks-ninja-portrait">${renderNinjaOutfitImage(ninja.appearanceId, ninja.name, { className: "barracks-ninja-outfit", loading: "eager" })}</div><div class="barracks-ninja-info"><strong>${escapeHtml(ninja.name)}</strong><span>${escapeHtml(ninja.rarity.toUpperCase())} · Lv ${ninja.level} · ${escapeHtml(status)}</span></div><button type="button" class="button button-danger" data-dismiss-ninja="${ninja.id}" ${ninja.canDismiss ? "" : `disabled title="${escapeHtml(disabledReason)}"`}>Dismiss</button></article>`;
      })
      .join("");
    content.innerHTML = `<div class="page-heading"><div><p class="eyebrow">VILLAGE OPERATIONS</p><h1>Barracks</h1><p class="muted">Manage your village roster and prepare squads for field operations.</p></div><span class="season-badge">LEVEL ${view.barracks.level}/${view.barracks.maxLevel}</span></div>
      <section class="barracks-summary" role="group" aria-label="Barracks capacity summary"><article title="Owned ninjas occupy roster space, including injured or active ninjas."><span class="eyebrow">RESIDENTIAL CAPACITY</span><strong>${view.rosterCount} / ${currentLevel.rosterCapacity}</strong></article><article title="Maximum ninjas assigned to outside operations and to one squad."><span class="eyebrow">EXTERNAL CAPACITY</span><strong>${currentLevel.externalCapacity}</strong></article><article title="Saved squad lineups and map destinations."><span class="eyebrow">MOBILIZATION PRESETS</span><strong>${view.presets.length} / ${currentLevel.mobilizationPresets}</strong></article></section>
      <nav class="section-tabs" role="tablist" aria-label="Barracks sections">
        <button id="barracks-tab-roster" class="section-tab" type="button" role="tab" aria-selected="true" aria-controls="barracks-panel-roster" tabindex="0" data-section-tab="roster">Roster management</button>
        <button id="barracks-tab-presets" class="section-tab" type="button" role="tab" aria-selected="false" aria-controls="barracks-panel-presets" tabindex="-1" data-section-tab="presets">Mobilization presets</button>
        <button id="barracks-tab-status" class="section-tab" type="button" role="tab" aria-selected="false" aria-controls="barracks-panel-status" tabindex="-1" data-section-tab="status">Status</button>
      </nav>
      <section id="barracks-panel-roster" role="tabpanel" tabindex="0" aria-labelledby="barracks-tab-roster" data-section-panel="roster">
        <section class="barracks-section"><div class="section-heading"><div><p class="eyebrow">ROSTER MANAGEMENT</p><h2>Dismiss a ninja</h2></div><span class="muted">Permanent, free, and without rewards. Equipment returns to the Warehouse.</span></div>${view.foundationRemaining > 0 ? `<p class="barracks-notice">Finish all ${view.foundationRemaining} guided Foundation recruit${view.foundationRemaining === 1 ? "" : "s"} before dismissing anyone.</p>` : ""}<div class="barracks-ninja-list">${dismissalRows || '<div class="empty-state">No ninjas in the village roster.</div>'}</div></section>
      </section>
      <section id="barracks-panel-presets" role="tabpanel" tabindex="0" aria-labelledby="barracks-tab-presets" data-section-panel="presets" hidden>
        <section class="barracks-section"><div class="section-heading"><div><p class="eyebrow">LOGISTICS</p><h2>Mobilization presets</h2></div><span class="muted">Save and activate a squad for a map.</span></div><div class="barracks-preset-grid">${presetCards}</div>${currentLevel.mobilizationPresets === 0 ? '<div class="empty-state">Mobilization presets unlock at Barracks level 7.</div>' : ""}</section>
      </section>
      <section id="barracks-panel-status" role="tabpanel" tabindex="0" aria-labelledby="barracks-tab-status" data-section-panel="status" hidden>
        <section class="barracks-section"><div class="section-heading"><div><p class="eyebrow">LEVEL CONFIGURATION</p><h2>Barracks effects</h2></div></div><div class="building-level-table-wrap"><table class="building-level-table"><thead><tr><th>LEVEL</th><th>ROSTER MAX</th><th>EXTERNAL MAX</th><th>PRESETS</th></tr></thead><tbody>${levelRows}</tbody></table></div></section>
      </section><p id="notice" class="notice" role="status"></p>`;
    new SectionTabs(content);

    content
      .querySelectorAll<HTMLFormElement>("[data-barracks-preset]")
      .forEach((form) => {
        form.querySelector<HTMLSelectElement>('[name="mapId"]')!.value =
          presetBySlot.get(Number(form.dataset.barracksPreset))?.mapId ??
          view.maps.find((map) => map.id === DEFAULT_MAP_ID)?.id ??
          view.maps[0]?.id ??
          "";
        form.addEventListener("change", (event) => {
          if (
            !(event.target instanceof HTMLInputElement) ||
            event.target.name !== "ninjaId"
          )
            return;
          const checked = form.querySelectorAll<HTMLInputElement>(
            '[name="ninjaId"]:checked',
          );
          if (checked.length > currentLevel.externalCapacity) {
            event.target.checked = false;
            setNotice(
              `This Barracks level supports up to ${currentLevel.externalCapacity} ninjas per preset.`,
            );
          }
        });
        form.addEventListener("submit", async (event) => {
          event.preventDefault();
          const slot = Number(form.dataset.barracksPreset);
          const ninjaIds = [
            ...form.querySelectorAll<HTMLInputElement>(
              '[name="ninjaId"]:checked',
            ),
          ].map((input) => input.value);
          const submit = form.querySelector<HTMLButtonElement>(
            'button[type="submit"]',
          );
          if (submit) submit.disabled = true;
          try {
            await api(`/barracks/presets/${slot}`, {
              method: "PUT",
              body: JSON.stringify({
                name: form.querySelector<HTMLInputElement>('[name="name"]')!
                  .value,
                mapId:
                  form.querySelector<HTMLSelectElement>('[name="mapId"]')!
                    .value,
                ninjaIds,
              }),
            });
            await showDashboard("barracks");
            setNotice("Mobilization preset saved.", "success");
          } catch (error) {
            setNotice((error as Error).message);
            if (submit) submit.disabled = false;
          }
        });
      });
    content
      .querySelectorAll<HTMLButtonElement>("[data-dismiss-ninja]")
      .forEach((button) => {
        button.addEventListener("click", async () => {
          const ninja = view.ninjas.find(
            (entry) => entry.id === button.dataset.dismissNinja,
          );
          if (
            !ninja ||
            !window.confirm(
              `Permanently dismiss ${ninja.name}? This cannot be undone. Equipped items return to the Warehouse.`,
            )
          )
            return;
          button.disabled = true;
          try {
            await api(`/barracks/ninjas/${ninja.id}/dismiss`, {
              method: "POST",
              headers: { "Idempotency-Key": crypto.randomUUID() },
              body: "{}",
            });
            await showDashboard("barracks");
            setNotice(
              `${ninja.name} was dismissed and the roster slot was released.`,
              "success",
            );
          } catch (error) {
            setNotice((error as Error).message);
            button.disabled = false;
          }
        });
      });
    content
      .querySelectorAll<HTMLButtonElement>("[data-activate-preset]")
      .forEach((button) => {
        button.addEventListener("click", async () => {
          button.disabled = true;
          try {
            const result = await api<{ mapId: string }>(
              `/barracks/presets/${button.dataset.activatePreset}/activate`,
              { method: "POST", body: "{}" },
            );
            await gameSessionLifecycle.enterGame("pve", result.mapId, button);
          } catch (error) {
            setNotice((error as Error).message);
            button.disabled = false;
          }
        });
      });
    content
      .querySelectorAll<HTMLButtonElement>("[data-delete-preset]")
      .forEach((button) => {
        button.addEventListener("click", async () => {
          if (!window.confirm("Delete this mobilization preset?")) return;
          button.disabled = true;
          try {
            await api(`/barracks/presets/${button.dataset.deletePreset}`, {
              method: "DELETE",
            });
            await showDashboard("barracks");
            setNotice("Mobilization preset deleted.", "success");
          } catch (error) {
            setNotice((error as Error).message);
            button.disabled = false;
          }
        });
      });
  } catch (error) {
    content.innerHTML = `<div class="page-heading"><div><p class="eyebrow">VILLAGE OPERATIONS</p><h1>Barracks</h1></div></div><div class="empty-state">${escapeHtml((error as Error).message)}</div>`;
  }
}

function showWarehouse(content: HTMLElement): Promise<void> {
  const page = new WarehousePage(content, {
    request: api,
    renderOwnedItem,
    onOpenItem: showItemDetails,
    itemCategoryLabel,
    equipmentSlotLabel,
  });
  warehousePage = page;
  return page.mount();
}
function renderOwnedItem(item: OwnedItemView): string {
  const progression = describeItemProgression(item.tier, item.enhancementLevel);
  const accessibleLabel = [
    item.name,
    item.rarity,
    ...progression,
    ...(item.quantity > 1 ? [`Quantity ${item.quantity}`] : []),
  ].join(", ");

  return `<button type="button" class="owned-item-button" data-owned-item="${escapeHtml(item.id)}" data-name="${escapeHtml(`${item.name} ${item.itemId}`.toLocaleLowerCase())}" data-category="${escapeHtml(item.category)}" data-slot="${escapeHtml(item.slot ?? "")}" data-rarity="${escapeHtml(item.rarity)}" data-tier="${item.tier}" aria-label="${escapeHtml(accessibleLabel)}">${ownedItemVisual(item, "owned-item-icon")}</button>`;
}

function ownedItemVisual(item: OwnedItemView, className: string): string {
  const rarity =
    RARITY_PRESENTATION[
      item.rarity.toLocaleLowerCase() as keyof typeof RARITY_PRESENTATION
    ] ?? RARITY_PRESENTATION.common;
  const icon = ITEM_ICONS[item.iconKey as keyof typeof ITEM_ICONS];
  const definition = itemDefinition(item.itemId);
  const isTechniqueScroll =
    definition?.category === "consumable" &&
    definition.usage?.kind === "technique_scroll";
  const scrollRarityIcon = isTechniqueScroll
    ? TECHNIQUE_SCROLL_RARITY_ICONS[
        item.rarity.toLocaleLowerCase() as keyof typeof TECHNIQUE_SCROLL_RARITY_ICONS
      ]
    : undefined;
  const image = icon
    ? `<img src="/${icon.path}" width="${icon.width}" height="${icon.height}" alt="" loading="lazy" />`
    : `<span class="owned-item-fallback">${escapeHtml(item.name.slice(0, 1).toLocaleUpperCase())}</span>`;
  const scrollMarker = scrollRarityIcon
    ? `<img class="technique-scroll-marker" src="/${scrollRarityIcon.path}" width="${scrollRarityIcon.width}" height="${scrollRarityIcon.height}" alt="${escapeHtml(rarity.label)} technique scroll" title="${escapeHtml(rarity.label)} technique scroll" />`
    : "";
  const tierLabel = formatItemTier(item.tier);
  const enhancementLabel = formatItemEnhancement(item.enhancementLevel);
  return `<span class="${className}" style="--item-rarity:${rarity.color}">${image}${scrollMarker}${tierLabel ? `<span class="item-tier-badge">${tierLabel}</span>` : ""}${enhancementLabel ? `<span class="item-enhancement-badge">${enhancementLabel}</span>` : ""}${item.stackable || item.quantity > 1 ? `<span class="item-quantity-badge">${item.quantity.toLocaleString()}</span>` : ""}</span>`;
}

function showItemDetails(item: OwnedItemView): void {
  document.getElementById("item-details-modal")?.remove();
  const rarity =
    RARITY_PRESENTATION[item.rarity as keyof typeof RARITY_PRESENTATION] ??
    RARITY_PRESENTATION.common;
  const modal = document.createElement("div");
  modal.id = "item-details-modal";
  modal.className = "inventory-modal-backdrop item-details-backdrop";
  const attributes = item.attributes.length
    ? `<ul class="item-details-attributes">${item.attributes
        .map(
          (attribute) =>
            `<li><span>${equipmentStatLabel(attribute.stat)}</span><b>${formatAttributeValue(attribute.value)}${attribute.unit === "percent" ? "%" : ""}</b></li>`,
        )
        .join("")}</ul>`
    : '<div class="empty-state item-no-attributes">No combat attributes</div>';
  const itemType = item.slot
    ? `${itemCategoryLabel(item.category)} · ${equipmentSlotLabel(item.slot)}`
    : itemCategoryLabel(item.category);
  const progression = formatItemProgression(item.tier, item.enhancementLevel);
  modal.style.setProperty("--rarity", rarity.color);
  modal.innerHTML = `<section class="item-details-dialog" role="dialog" aria-modal="true" aria-labelledby="item-details-title"><header class="item-details-header"><div><p class="eyebrow">${escapeHtml(itemType)}</p><h2 id="item-details-title">${escapeHtml(item.name)}</h2></div><button type="button" class="inventory-modal-close" aria-label="Close">×</button></header><div class="item-details-body"><div class="item-details-summary"><div class="item-details-icon">${ownedItemVisual(item, "owned-item-icon")}</div><div class="item-quality-details"><strong style="color:${rarity.color}">${escapeHtml(rarity.label)}</strong>${progression ? `<span>${escapeHtml(progression)}</span>` : ""}${item.stackable || item.quantity > 1 ? `<span>Quantity ${item.quantity.toLocaleString()}</span>` : ""}</div></div><p class="item-details-description">${escapeHtml(item.description)}</p>${attributes}<dl class="item-details-meta"><div><dt>Item ID</dt><dd>${escapeHtml(item.itemId)}</dd></div><div><dt>Unit volume</dt><dd>${formatAttributeValue(item.unitVolume)}</dd></div></dl></div></section>`;
  document.body.append(modal);
  const close = (): void => {
    window.removeEventListener("keydown", onKeyDown);
    modal.remove();
  };
  modal
    .querySelector(".inventory-modal-close")
    ?.addEventListener("click", close);
  modal.addEventListener("click", (event) => {
    if (event.target === modal) close();
  });
  function onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") close();
  }
  window.addEventListener("keydown", onKeyDown);
}

function itemCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    equipment: "Equipment",
    resource: "Resource",
    material: "Material",
    consumable: "Consumable",
  };
  return labels[category] ?? escapeHtml(category);
}

async function showNinjaInventory(ninja: Ninja): Promise<void> {
  document.getElementById("ninja-inventory-modal")?.remove();
  let view: NinjaInventoryView;
  try {
    view = await api<NinjaInventoryView>(
      `/ninjas/${encodeURIComponent(ninja.id)}/inventory`,
    );
  } catch (error) {
    setNotice((error as Error).message);
    return;
  }
  const rarity =
    RARITY_PRESENTATION[view.ninja.rarity] ?? RARITY_PRESENTATION.common;
  const modal = document.createElement("div");
  modal.id = "ninja-inventory-modal";
  modal.className = "inventory-modal-backdrop";
  modal.style.setProperty("--rarity", rarity.color);
  modal.innerHTML = `<section class="inventory-modal" role="dialog" aria-modal="true" aria-labelledby="ninja-inventory-title"><header class="inventory-modal-header"><div class="inventory-modal-portrait">${renderNinjaOutfitImage(view.ninja.appearanceId, view.ninja.name, { className: "inventory-modal-outfit", loading: "eager" })}</div><div><p class="eyebrow">${escapeHtml(rarity.label)} · ${escapeHtml(view.ninja.rank)}</p><h2 id="ninja-inventory-title">${escapeHtml(view.ninja.name)}</h2><small>LEVEL ${view.ninja.level}</small></div><button type="button" class="inventory-modal-close" aria-label="Close">×</button></header><nav class="inventory-tabs" role="tablist"><button class="selected" role="tab" aria-selected="true" data-inventory-tab="inventory">Inventory</button><button role="tab" aria-selected="false" data-inventory-tab="status">Status</button><button role="tab" aria-selected="false" data-inventory-tab="backpack">Backpack</button></nav><div class="inventory-tab-panel" role="tabpanel"></div></section>`;
  document.body.append(modal);
  const jutsuTabButton = document.createElement("button");
  jutsuTabButton.type = "button";
  jutsuTabButton.setAttribute("role", "tab");
  jutsuTabButton.setAttribute("aria-selected", "false");
  jutsuTabButton.dataset.inventoryTab = "jutsus";
  jutsuTabButton.textContent = "Jutsus";
  modal.querySelector(".inventory-tabs")?.append(jutsuTabButton);
  const panel = modal.querySelector<HTMLElement>(".inventory-tab-panel")!;
  let activeTab = "inventory";
  let equipmentChanged = false;
  const equipmentUnavailableMessage = (() => {
    if (
      view.ninja.lifeStatus === "incapacitated" ||
      (typeof view.ninja.currentHealth === "number" &&
        view.ninja.currentHealth <= 0)
    ) {
      return "This ninja is incapacitated. Complete recovery at the Hospital before changing equipment.";
    }
    if (view.ninja.lifeStatus === "hospitalized") {
      return "This ninja is recovering at the Hospital. Equipment can be changed after treatment.";
    }
    if (view.ninja.activityLock) {
      return "This ninja is assigned to an active activity. Return them to the village before changing equipment.";
    }
    if (
      view.ninja.lifeStatus !== undefined &&
      view.ninja.lifeStatus !== "available"
    ) {
      return "This ninja is not available in the village.";
    }
    // Availability fields can be absent when the frontend and API are on different revisions.
    // Allow the player to open the picker; the API still authoritatively rejects invalid changes.
    return null;
  })();
  const jutsuUnavailableMessage = (() => {
    if (
      view.ninja.lifeStatus === "incapacitated" ||
      (typeof view.ninja.currentHealth === "number" &&
        view.ninja.currentHealth <= 0)
    ) {
      return "This ninja is incapacitated. Complete recovery at the Hospital before changing jutsu.";
    }
    if (view.ninja.lifeStatus === "hospitalized") {
      return "This ninja is recovering at the Hospital. Jutsu can be changed after treatment.";
    }
    if (view.ninja.activityLock) {
      return "This ninja is assigned to an active activity. Return them to the village before changing jutsu.";
    }
    if (
      view.ninja.lifeStatus !== undefined &&
      view.ninja.lifeStatus !== "available"
    ) {
      return "This ninja is not available in the village.";
    }
    return null;
  })();
  let jutsuLoadoutTab: JutsuLoadoutTab | null = null;
  const openEquipmentPicker = async (slot: EquipmentSlot): Promise<void> => {
    modal.querySelector(".equipment-picker-backdrop")?.remove();
    const picker = document.createElement("div");
    picker.className = "equipment-picker-backdrop";
    picker.innerHTML = `<section class="equipment-picker-dialog" role="dialog" aria-modal="true" aria-labelledby="equipment-picker-title"><header class="equipment-picker-header"><div><p class="eyebrow">NINJA EQUIPMENT</p><h3 id="equipment-picker-title">${escapeHtml(equipmentSlotLabel(slot))}</h3></div><button type="button" class="inventory-modal-close" aria-label="Close equipment picker">×</button></header><div class="equipment-picker-content"><p class="inventory-tab-intro">Choose an owned item in the same slot. Equipping it returns the previous item to the Warehouse.</p><div class="equipment-picker-items"></div><p class="equipment-picker-feedback" role="status"></p></div></section>`;
    modal.append(picker);
    const closePicker = (): void => picker.remove();
    picker
      .querySelector(".inventory-modal-close")
      ?.addEventListener("click", closePicker);
    picker.addEventListener("click", (event) => {
      if (event.target === picker) closePicker();
    });
    const itemsContainer = picker.querySelector<HTMLElement>(
      ".equipment-picker-items",
    )!;
    const feedback = picker.querySelector<HTMLElement>(
      ".equipment-picker-feedback",
    )!;
    try {
      const warehouseView = await api<WarehouseView>("/warehouse");
      const candidates = warehouseView.items.filter(
        (item) =>
          item.category === "equipment" &&
          item.slot === slot &&
          item.location === "warehouse",
      );
      const current = view.equipmentSlots.find(
        (entry) => entry.slot === slot,
      )?.item;
      const unequipAction = current
        ? '<button type="button" class="button button-outline equipment-unequip" data-equipment-unequip>Unequip current item</button>'
        : "";
      const candidateMarkup = candidates
        .map((item) => {
          const progression = formatItemProgression(
            item.tier,
            item.enhancementLevel,
          );
          const quality = [item.rarity, progression]
            .filter(Boolean)
            .join(" · ");
          return `<button type="button" class="equipment-picker-item" data-equip-item="${escapeHtml(item.id)}"><span class="equipment-picker-icon">${ownedItemVisual(item, "owned-item-icon")}</span><span class="equipment-picker-copy"><strong>${escapeHtml(item.name)}</strong><small>${escapeHtml(quality)}</small><span>${item.attributes.map((attribute) => `${escapeHtml(equipmentStatLabel(attribute.stat))} +${formatAttributeValue(attribute.value)}${attribute.unit === "percent" ? "%" : ""}`).join(" · ")}</span></span><span class="equipment-picker-action">Equip</span></button>`;
        })
        .join("");
      itemsContainer.innerHTML = `${unequipAction}${candidateMarkup || '<div class="empty-state equipment-picker-empty">No owned Warehouse items fit this slot.</div>'}`;
      picker
        .querySelector<HTMLButtonElement>("[data-equipment-unequip]")
        ?.addEventListener("click", async (event) => {
          const button = event.currentTarget as HTMLButtonElement;
          button.disabled = true;
          try {
            view = await api<NinjaInventoryView>(
              `/ninjas/${encodeURIComponent(ninja.id)}/equipment/${encodeURIComponent(slot)}`,
              { method: "DELETE" },
            );
            equipmentChanged = true;
            closePicker();
            renderTab(activeTab);
          } catch (error) {
            feedback.textContent = (error as Error).message;
            button.disabled = false;
          }
        });
      picker
        .querySelectorAll<HTMLButtonElement>("[data-equip-item]")
        .forEach((button) =>
          button.addEventListener("click", async () => {
            button.disabled = true;
            try {
              view = await api<NinjaInventoryView>(
                `/ninjas/${encodeURIComponent(ninja.id)}/equipment/${encodeURIComponent(slot)}`,
                {
                  method: "PUT",
                  body: JSON.stringify({
                    itemInstanceId: button.dataset.equipItem,
                  }),
                },
              );
              equipmentChanged = true;
              closePicker();
              renderTab(activeTab);
            } catch (error) {
              feedback.textContent = (error as Error).message;
              button.disabled = false;
            }
          }),
        );
    } catch (error) {
      itemsContainer.innerHTML = `<div class="empty-state">${escapeHtml((error as Error).message)}</div>`;
    }
  };
  const renderTab = (tab: string): void => {
    activeTab = tab;
    modal
      .querySelectorAll<HTMLButtonElement>("[data-inventory-tab]")
      .forEach((button) => {
        const selected = button.dataset.inventoryTab === tab;
        button.classList.toggle("selected", selected);
        button.setAttribute("aria-selected", String(selected));
      });
    if (tab === "inventory") {
      const equipmentIntro = equipmentUnavailableMessage
        ? `${equipmentUnavailableMessage} You can open a slot; the server checks eligibility when you equip or unequip.`
        : "Select a slot to equip, replace, or unequip an item from the village Warehouse.";
      panel.innerHTML = `<p class="inventory-tab-intro">${escapeHtml(equipmentIntro)}</p><div class="ninja-equipment-slots">${view.equipmentSlots
        .map(({ slot, item }) => {
          const icon = ITEM_ICONS[slot];
          const progression = item
            ? formatItemProgression(item.tier, item.enhancementLevel)
            : "";
          const quality = item
            ? [item.rarity, progression].filter(Boolean).join(" · ")
            : "";
          return `<button type="button" class="ninja-equipment-slot ${item ? "occupied" : "empty"}" data-equipment-action-slot="${slot}"><span class="ninja-equipment-slot-icon">${item ? ownedItemVisual(item, "owned-item-icon") : `<img src="/${icon.path}" alt="" />`}</span><small>${equipmentSlotLabel(slot)}</small>${item ? `<strong>${escapeHtml(item.name)}</strong><span>${escapeHtml(quality)}</span>` : "<strong>Empty</strong><span>Click to equip</span>"}</button>`;
        })
        .join("")}</div>`;
    } else if (tab === "status") {
      const labels: Record<string, string> = {
        health: "Max Health",
        chakra: "Max Chakra",
        physicalAttack: "Physical Attack",
        techniqueAttack: "Technique Attack",
        physicalDefense: "Physical Defense",
        techniqueDefense: "Technique Defense",
        precision: "Precision",
        evasion: "Evasion",
        critical: "Critical",
        criticalResistance: "Critical Resistance",
        criticalDamage: "Critical Damage",
        criticalDamageReduction: "Critical Damage Reduction",
        physicalPenetration: "Physical Penetration",
        techniquePenetration: "Technique Penetration",
        controlPower: "Control Power",
        tenacity: "Tenacity",
      };
      const attributes = Object.entries(view.ninja.attributes)
        .filter(([, value]) => Number.isFinite(value))
        .sort(([left], [right]) => left.localeCompare(right))
        .map(
          ([key, value]) =>
            `<article><span>${labels[key] ?? escapeHtml(key)}</span><b>${formatAttributeValue(Number(value))}</b></article>`,
        )
        .join("");
      panel.innerHTML = `<div class="ninja-status-vitals"><article><span>HEALTH</span><b>${view.ninja.currentHealth} / ${view.ninja.maximumHealth}</b></article><article><span>CHAKRA</span><b>${view.ninja.currentChakra} / ${view.ninja.maximumChakra}</b></article></div><div class="ninja-status-attributes">${attributes}</div><p class="inventory-tab-intro">Server-composed attributes include this ninja's currently equipped item bonuses.</p>`;
    } else if (tab === "jutsus") {
      if (!jutsuLoadoutTab) {
        jutsuLoadoutTab = new JutsuLoadoutTab(panel, {
          ninjaId: view.ninja.id,
          ninjaRarity: view.ninja.rarity,
          canEdit: jutsuUnavailableMessage === null,
          unavailableMessage: jutsuUnavailableMessage,
          isActive: () => activeTab === "jutsus",
          load: async (ninjaId): Promise<JutsuLibraryData> => {
            const response = await api<NinjaTechniqueLibraryView>(
              `/ninjas/${encodeURIComponent(ninjaId)}/techniques`,
            );
            return {
              known: response.known.map((entry) => ({
                techniqueId: entry.technique_id,
                techniqueLevel: entry.technique_level,
              })),
              equipped: response.equipped.map((entry) => ({
                techniqueId: entry.technique_id,
                slotIndex: entry.slot_index,
              })),
            };
          },
          save: async (ninjaId, slots): Promise<JutsuLoadoutSlot[]> => {
            const response = await api<{ slots: JutsuLoadoutSlot[] }>(
              `/ninjas/${encodeURIComponent(ninjaId)}/loadout`,
              {
                method: "PUT",
                body: JSON.stringify({ slots }),
              },
            );
            return response.slots;
          },
        });
      }
      void jutsuLoadoutTab.activate();
      return;
    } else {
      panel.innerHTML = view.backpack.items.length
        ? `<p class="inventory-tab-intro">Cargo belongs to this ninja's field assignment and settles into the shared Warehouse on return.</p><div class="owned-item-grid backpack-owned-item-grid">${view.backpack.items.map((item) => renderOwnedItem(item)).join("")}</div>`
        : '<div class="empty-state backpack-empty"><span>◌</span><h3>No assignment cargo</h3><p>Items carried during a field assignment will appear here. Returned cargo is settled into the shared Warehouse.</p></div>';
    }
    panel
      .querySelectorAll<HTMLButtonElement>("[data-owned-item]")
      .forEach((tile) => {
        const item = view.backpack.items.find(
          (entry) => entry.id === tile.dataset.ownedItem,
        );
        if (item) tile.addEventListener("click", () => showItemDetails(item));
      });
    panel
      .querySelectorAll<HTMLButtonElement>("[data-equipment-action-slot]")
      .forEach((button) =>
        button.addEventListener(
          "click",
          () =>
            void openEquipmentPicker(
              button.dataset.equipmentActionSlot as EquipmentSlot,
            ),
        ),
      );
  };
  renderTab("inventory");
  modal
    .querySelectorAll<HTMLButtonElement>("[data-inventory-tab]")
    .forEach((button) =>
      button.addEventListener("click", () =>
        renderTab(button.dataset.inventoryTab!),
      ),
    );
  const close = (): void => {
    window.removeEventListener("keydown", onKeyDown);
    modal.remove();
    if (equipmentChanged) void showDashboard("roster");
  };
  modal
    .querySelector(".inventory-modal-close")
    ?.addEventListener("click", close);
  modal.addEventListener("click", (event) => {
    if (event.target === modal) close();
  });
  function onKeyDown(event: KeyboardEvent): void {
    if (event.key !== "Escape") return;
    const confirmation = modal.querySelector<HTMLElement>(
      ".jutsu-confirm-backdrop",
    );
    if (confirmation) {
      if (confirmation.dataset.saving !== "true") confirmation.remove();
      return;
    }
    close();
  }
  window.addEventListener("keydown", onKeyDown);
}

function equipmentSlotLabel(slot: EquipmentSlot): string {
  const labels: Record<EquipmentSlot, string> = {
    weapon: "Weapon",
    secondary_weapon: "Secondary weapon",
    helmet: "Helmet",
    armor: "Armor",
    legs: "Legs",
    glove: "Gloves",
    boots: "Boots",
    ring: "Ring",
    earring: "Earring",
    necklace: "Necklace",
    bracelet: "Bracelet",
  };
  return labels[slot];
}

function equipmentStatLabel(stat: string): string {
  const labels: Record<string, string> = {
    maxHealth: "Max health",
    maxChakra: "Max Chakra",
    physicalAttack: "Physical attack",
    techniqueAttack: "Technique attack",
    physicalDefense: "Physical defense",
    techniqueDefense: "Technique defense",
    critical: "Critical",
    precision: "Precision",
    evasion: "Evasion",
    movementSpeedPercent: "Movement speed",
    attackSpeedPercent: "Attack speed",
    controlPower: "Control power",
    tenacity: "Tenacity",
  };
  return labels[stat] ?? stat;
}

function formatAttributeValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function destroyHospitalPage(): void {
  hospitalPage?.destroy();
  hospitalPage = null;
}

function destroyWarehousePage(): void {
  warehousePage?.destroy();
  warehousePage = null;
}

function showHospital(content: HTMLElement): Promise<void> {
  const page = new HospitalPage(content, {
    request: api,
    onOpenRoster: () => {
      void showDashboard("roster");
    },
  });
  hospitalPage = page;
  return page.mount();
}
function showAcademy(content: HTMLElement, ninjas: Ninja[]): Promise<void> {
  const page = new AcademyPage(content, {
    request: api,
    ninjas,
    onRefresh: () => showDashboard("academy"),
  });
  academyPage = page;
  return page.mount();
}

async function showGame(
  room: GameRoom,
  mode: GameMode,
  selectedMapId: string | undefined,
  progress: GameMountProgress,
): Promise<void> {
  destroyWarehousePage();
  academyPage?.destroy();
  academyPage = null;
  if (room.reconnectionToken)
    sessionStorage.setItem(ROOM_TOKEN_KEY, room.reconnectionToken);
  const showDiagnostics =
    import.meta.env.DEV && new URLSearchParams(location.search).has("debug");
  const mapId = selectedMapId ?? room.state.mapId ?? DEFAULT_MAP_ID;
  const activeMap = await loadMapDefinition(mapId);
  if (!activeMap) throw new Error(`Unknown game map: ${mapId}`);
  if (
    room.state.mapId !== activeMap.id ||
    room.state.mapRevision !== activeMap.revision
  )
    throw new Error(
      `Map content mismatch: server ${room.state.mapId}@${room.state.mapRevision || "missing"}, client ${activeMap.id}@${activeMap.revision}`,
    );
  document.body.classList.add("playing");
  const gameTitle =
    mode === "pvp"
      ? "EXPERIMENTAL ARENA · 1V1"
      : activeMap.status === "preview"
        ? `${activeMap.name.toUpperCase()} · MAP PREVIEW · NO COMBAT`
        : `${activeMap.name.toUpperCase()} · PRIVATE INSTANCE`;
  app!.innerHTML = `
    <main class="game-shell">
      <header class="game-topbar">
        <div>
          <span class="wordmark">
            <span class="wordmark-icon-shell" aria-hidden="true">
              ${renderBrandIcon({ size: "large", className: "wordmark-brand-icon" })}
            </span>
            SHINOBI IDLE
          </span>
          <span class="mode-label">${gameTitle}</span>
        </div>
        <div class="connection-state" id="connection"><i></i> CONNECTING</div>
        <div id="loot-history-toggle-mount" class="loot-history-toggle-mount"></div>
        <button
          id="leave-game"
          class="button button-quiet game-leave-button"
          aria-label="Return to village"
          title="Return to village"
        >
          <img src="/assets/interface/village-return.webp" alt="" width="32" height="32" />
        </button>
      </header>
      <section id="game-mount" class="game-mount"></section>
      <div
        class="game-camera-controls"
        id="game-camera-controls"
        role="group"
        aria-label="Camera controls"
      >
        <button type="button" data-camera-action="zoom-out" aria-label="Zoom out" title="Zoom out">−</button>
        <button type="button" data-camera-action="center" aria-label="Center on your ninjas" title="Center on your ninjas">◎</button>
        <button type="button" data-camera-action="zoom-in" aria-label="Zoom in" title="Zoom in">+</button>
      </div>
      <div class="game-orientation-hint" role="status">
        Rotate your device to landscape for a wider view.
      </div>
      <div id="game-message" class="game-message"></div>
      <aside id="loot-feedback-root"></aside>
      ${showDiagnostics ? '<pre id="game-diagnostics" aria-label="Game diagnostics"></pre>' : ""}
    </main>`;
  const updateRoomStatus = (state: InstanceStateLike): void => {
    if (!state.phase) return;
    const connection = document.querySelector<HTMLElement>("#connection");
    if (connection)
      connection.innerHTML = `<i></i> ${state.phase === "waiting" ? "WAITING FOR OPPONENT" : "CONNECTED · LIVE"}`;
  };
  updateRoomStatus(room.state);
  room.onStateChange(updateRoomStatus);
  let regionTransitionStarted = false;
  let regionTransitionCommitted = false;
  const bossEntryDialog = new BossEntryConfirmationDialog({
    onConfirm: (accessId) =>
      room.send("region.transition.confirm", { accessId }),
    onCancel: (accessId) =>
      room.send("region.transition.dismiss", { accessId }),
  });
  room.onMessage("region.transition.committed", () => {
    regionTransitionCommitted = true;
    leavingGame = true;
    sessionStorage.removeItem(ROOM_TOKEN_KEY);
  });
  room.onMessage(
    "region.transition_ready",
    (event: {
      proof: string;
      fromMapId: string;
      toMapId: string;
      spawnId: string;
      accessId?: string;
    }) => {
      bossEntryDialog.close(event.accessId);
      if (regionTransitionStarted) return;
      regionTransitionStarted = true;
      void gameSessionLifecycle
        .transitionToRegionFloor(room, event)
        .finally(() => {
          regionTransitionStarted = false;
        });
    },
  );
  room.onMessage(
    "region.transition_confirmation_required",
    (event: {
      accessId: string;
      regionName?: string;
      bossName?: string;
      sealLabel?: string;
      preparedSeals?: number;
      cooldownHours?: number;
    }) => bossEntryDialog.show(event),
  );
  room.onMessage(
    "region.transition_confirmation_closed",
    (event: { accessId?: string }) => bossEntryDialog.close(event.accessId),
  );
  room.onMessage(
    "region.transition_check_failed",
    (event: { accessId?: string }) => bossEntryDialog.retry(event.accessId),
  );
  room.onMessage(
    "region.transition_denied",
    (event: { accessId?: string; message?: string }) => {
      bossEntryDialog.close(event.accessId);
      const message = document.querySelector<HTMLElement>("#game-message");
      if (!message) return;
      message.textContent = event.message ?? "This entrance is unavailable.";
      message.classList.add("visible");
      window.setTimeout(() => message.classList.remove("visible"), 3_000);
    },
  );
  room.onMessage("combat.reward", (event: { ryo?: number }) => {
    const message = document.querySelector<HTMLElement>("#game-message");
    if (!message || !event.ryo) return;
    message.textContent = `+${event.ryo.toLocaleString()} Ryo`;
    message.classList.add("visible");
    window.setTimeout(() => message.classList.remove("visible"), 2_500);
  });
  room.onMessage(
    "region.progress",
    (event: {
      regionName?: string;
      marksLabel?: string;
      sealsLabel?: string;
      marks?: number;
      preparedSeals?: number;
      maxPreparedSeals?: number;
    }) => {
      const message = document.querySelector<HTMLElement>("#game-message");
      if (!message) return;
      message.textContent = `${event.regionName ?? "Region"}: ${event.marks ?? 0} ${event.marksLabel ?? "Marks"} · ${event.preparedSeals ?? 0}/${event.maxPreparedSeals ?? 0} ${event.sealsLabel ?? "Seals"}`;
      message.classList.add("visible");
      window.setTimeout(() => message.classList.remove("visible"), 3_000);
    },
  );
  room.onMessage(
    "region.boss_victory",
    (event: {
      regionName?: string;
      bossName?: string;
      firstClear?: boolean;
      cooldownHours?: number;
    }) => {
      const message = document.querySelector<HTMLElement>("#game-message");
      if (!message) return;
      message.textContent = event.firstClear
        ? `${event.bossName ?? "Regional boss"} defeated for the first time in ${event.regionName ?? "this region"}.`
        : `Victory! ${event.bossName ?? "Regional boss"} cooldown: ${event.cooldownHours ?? 6} hours.`;
      message.classList.add("visible");
      window.setTimeout(() => message.classList.remove("visible"), 5_000);
    },
  );
  const mount = document.querySelector<HTMLElement>("#game-mount")!;
  const detailsDialog = new NinjaDetailsDialog({
    parent: document.querySelector<HTMLElement>(".game-shell")!,
    renderPortrait: portrait,
    onCloseRequest: () => undefined,
    loadProfile: async (ninjaId): Promise<NinjaDetailsProfile> => {
      const view = await api<NinjaInventoryView>(
        `/ninjas/${encodeURIComponent(ninjaId)}/inventory`,
      );
      let equippedTechniques: NinjaDetailsProfile["techniques"] = [];
      try {
        const library = await api<NinjaTechniqueLibraryView>(
          `/ninjas/${encodeURIComponent(ninjaId)}/techniques`,
        );
        const knownLevels = new Map(
          library.known.map((entry) => [
            entry.technique_id,
            entry.technique_level,
          ]),
        );
        equippedTechniques = library.equipped.map((entry) => {
          const definition =
            TECHNIQUES[entry.technique_id as keyof typeof TECHNIQUES];
          const icon =
            TECHNIQUE_ICONS[entry.technique_id as keyof typeof TECHNIQUE_ICONS];
          return {
            id: entry.technique_id,
            name:
              definition?.name ??
              entry.technique_id
                .replaceAll("_", " ")
                .replace(/\b\w/g, (character) => character.toUpperCase()),
            ...(icon ? { iconPath: icon.path } : {}),
            level: knownLevels.get(entry.technique_id) ?? 1,
          };
        });
      } catch {
        // Ninja status and equipment remain available if the optional jutsu
        // summary endpoint is temporarily unavailable.
      }

      const rarity =
        RARITY_PRESENTATION[view.ninja.rarity] ?? RARITY_PRESENTATION.common;
      return {
        name: view.ninja.name,
        appearanceId: view.ninja.appearanceId,
        rarity: rarity.label,
        rarityColor: rarity.color,
        rank: view.ninja.rank,
        level: view.ninja.level,
        ...(view.ninja.lifeStatus ? { lifeStatus: view.ninja.lifeStatus } : {}),
        attributes: view.ninja.attributes,
        equipment: view.equipmentSlots.map(({ slot, item }) => {
          if (!item) return { slot: equipmentSlotLabel(slot), item: null };
          const itemRarity =
            RARITY_PRESENTATION[
              item.rarity.toLowerCase() as keyof typeof RARITY_PRESENTATION
            ] ?? RARITY_PRESENTATION.common;
          const itemIcon = ITEM_ICONS[item.iconKey as keyof typeof ITEM_ICONS];
          return {
            slot: equipmentSlotLabel(slot),
            item: {
              name: item.name,
              ...(itemIcon ? { iconPath: itemIcon.path } : {}),
              rarity: itemRarity.label,
              rarityColor: itemRarity.color,
              tier: item.tier,
              enhancementLevel: item.enhancementLevel,
              attributes: item.attributes.map((attribute) => ({
                label: equipmentStatLabel(attribute.stat),
                value: attribute.value,
                unit: attribute.unit,
              })),
            },
          };
        }),
        techniques: equippedTechniques,
      };
    },
  });
  let rejectStartup: ((error: Error) => void) | undefined;
  let startupResolved = false;
  const startup = new Promise<void>((resolve, reject) => {
    let settled = false;
    const timeout = window.setTimeout(
      () =>
        finish(new Error("Timed out waiting for the game scene to render.")),
      60_000,
    );
    const finish = (error?: Error): void => {
      if (settled) return;
      settled = true;
      startupResolved = !error;
      window.clearTimeout(timeout);
      rejectStartup = undefined;
      if (error) reject(error);
      else resolve();
    };
    rejectStartup = finish;

    let disposeCameraControls = (): void => {};
    const gameScene = new GameScene(
      room,
      currentUser?.id ?? "",
      mode,
      {
        onFocusedNinjaChanged: (entity) => {
          if (!entity) {
            detailsDialog.setFocusedNinja(null);
            return;
          }
          const tile = worldPointToTile(
            { x: entity.x, y: entity.y },
            activeMap.tileSize,
          );
          const state: NinjaDetailsRealtimeState = {
            id: entity.persistentId || entity.id,
            name: entity.name,
            appearanceId: entity.appearanceId,
            hp: entity.hp,
            maxHp: entity.maxHp,
            chakra: entity.chakra,
            maxChakra: entity.maxChakra,
            alive: entity.alive,
            animation: entity.animation,
            tileColumn: tile.column,
            tileRow: tile.row,
          };
          detailsDialog.setFocusedNinja(state);
        },
        onShutdown: () => {
          detailsDialog.dispose();
          bossEntryDialog.dispose();
          disposeCameraControls();
        },
        onAssetProgress: progress.onAssetProgress,
        onAssetError: (error) => finish(error),
        onFirstFrame: () => {
          progress.onFirstFrame();
          finish();
        },
      },
      activeMap,
    );
    if (mode === "pve") room.send(ROOM_MESSAGES.regionEncounterTimersRequest);
    detailsDialog.setCloseHandler(() => gameScene.clearNinjaSelection());
    const cameraControls = document.querySelector<HTMLElement>(
      "#game-camera-controls",
    );
    const handleCameraControl = (event: Event): void => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>(
        "[data-camera-action]",
      );
      if (!button) return;
      switch (button.dataset.cameraAction) {
        case "zoom-in":
          gameScene.zoomCamera(0.25);
          break;
        case "zoom-out":
          gameScene.zoomCamera(-0.25);
          break;
        case "center":
          gameScene.centerCamera();
          break;
      }
    };
    cameraControls?.addEventListener("click", handleCameraControl);
    disposeCameraControls = () =>
      cameraControls?.removeEventListener("click", handleCameraControl);
    try {
      currentGame = new Phaser.Game({
        type: Phaser.CANVAS,
        parent: mount,
        width: 1024,
        height: 768,
        backgroundColor: "#081522",
        pixelArt: true,
        antialias: false,
        roundPixels: true,
        input: { activePointers: 2 },
        scale: {
          mode: Phaser.Scale.RESIZE,
          width: 1024,
          height: 768,
        },
        scene: gameScene,
      });
    } catch (error) {
      disposeCameraControls();
      finish(error instanceof Error ? error : new Error(String(error)));
    }
  });
  room.onDrop((code, reason) => {
    console.warn("Realtime room connection dropped", {
      roomId: room.roomId,
      sessionId: room.sessionId,
      mapId: room.state.mapId,
      code,
      reason,
      intentionalExit: intentionalRoomExits.has(room),
    });
  });
  room.onLeave((code, reason) => {
    const intentionalExit = intentionalRoomExits.has(room);
    console.info("Realtime room connection left", {
      roomId: room.roomId,
      sessionId: room.sessionId,
      mapId: room.state.mapId,
      code,
      reason,
      intentionalExit,
    });
    if (regionTransitionCommitted) {
      if (regionTransitionStarted) return;
      if (currentRoom === room) currentRoom = null;
      currentGame?.destroy(true);
      currentGame = null;
      document.body.classList.remove("playing");
      void showDashboard();
      return;
    }
    if (!intentionalExit && !leavingGame && room.reconnectionToken)
      sessionStorage.setItem(ROOM_TOKEN_KEY, room.reconnectionToken);
    if (!startupResolved)
      rejectStartup?.(
        new Error(`Room left before the game scene was ready (${code}).`),
      );
    if (
      !intentionalExit &&
      !leavingGame &&
      sessionStorage.getItem(ROOM_TOKEN_KEY)
    )
      window.setTimeout(() => window.location.reload(), 500);
    if (intentionalExit) return;
    const status = document.querySelector<HTMLElement>("#connection");
    if (status) {
      status.classList.add("disconnected");
      status.innerHTML = `<i></i> DISCONNECTED · ${code}`;
    }
  });
  document
    .querySelector<HTMLButtonElement>("#leave-game")!
    .addEventListener("click", () => {
      void gameSessionLifecycle.returnToVillage();
    });
  return startup;
}

void (async () => {
  if (await restoreSession()) {
    const roomToken = sessionStorage.getItem(ROOM_TOKEN_KEY);
    const roomResumed = roomToken
      ? await gameSessionLifecycle.resumeRoom(roomToken)
      : false;
    if (
      !roomResumed &&
      !(await gameSessionLifecycle.resumePendingRegionTransition())
    )
      await showDashboard();
  } else await showLanding();
})();
